import { inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { PosSessionsControllerFindAll$Params } from '@klubovna/api-client';
import { AddHelperDto, ClosePosSessionDto, OpenPosSessionDto } from '@klubovna/api-client';
import { Store } from '@ngrx/store';
import * as PosSessionsActions from './pos-sessions.actions';
import * as PosSessionsSelectors from './pos-sessions.selectors';

@Injectable({
  providedIn: 'root',
})
export class PosSessionsFacade {
  private store = inject(Store);
  private currentSessionFetched = false;

  sessions$ = this.store.select(PosSessionsSelectors.selectAllPosSessions);
  loading$ = this.store.select(PosSessionsSelectors.selectPosSessionsLoading);
  currentSessionLoading$ = this.store.select(PosSessionsSelectors.selectCurrentSessionLoading);
  currentSession$ = this.store.select(PosSessionsSelectors.selectCurrentPosSession);
  overallStats$ = this.store.select(PosSessionsSelectors.selectOverallPosStats);
  selectedSessionStats$ = this.store.select(PosSessionsSelectors.selectSelectedSessionStats);
  statsLoading$ = this.store.select(PosSessionsSelectors.selectStatsLoading);
  pagination$ = this.store.select(PosSessionsSelectors.selectPosSessionsPagination);
  params$ = this.store.select(PosSessionsSelectors.selectPosSessionsParams);
  helpers$ = this.store.select(PosSessionsSelectors.selectHelpers);
  helpersLoading$ = this.store.select(PosSessionsSelectors.selectHelpersLoading);

  sessions = toSignal(this.sessions$, { initialValue: [] });
  loading = toSignal(this.loading$, { initialValue: false });
  currentSessionLoading = toSignal(this.currentSessionLoading$, { initialValue: false });
  currentSession = toSignal(this.currentSession$, { initialValue: null });
  overallStats = toSignal(this.overallStats$, { initialValue: null });
  selectedSessionStats = toSignal(this.selectedSessionStats$, { initialValue: null });
  statsLoading = toSignal(this.statsLoading$, { initialValue: false });
  pagination = toSignal(this.pagination$);
  params = toSignal(this.params$);
  helpers = toSignal(this.helpers$, { initialValue: [] });
  helpersLoading = toSignal(this.helpersLoading$, { initialValue: false });

  loadSessions(params?: PosSessionsControllerFindAll$Params): void {
    this.store.dispatch(PosSessionsActions.loadPosSessions({ params }));
  }

  loadCurrentSession(force = false): void {
    if (this.currentSessionFetched && !force) {
      return;
    }

    const isLoading = this.currentSessionLoading();
    if (isLoading) {
      return;
    }

    this.currentSessionFetched = true;
    this.store.dispatch(PosSessionsActions.loadCurrentSession());
  }

  openSession(body: OpenPosSessionDto): void {
    this.store.dispatch(PosSessionsActions.openSession({ body }));
  }

  closeSession(id: string, body?: ClosePosSessionDto): void {
    this.currentSessionFetched = false;
    this.store.dispatch(PosSessionsActions.closeSession({ id, body }));
  }

  loadOverallStats(params?: { dateFrom?: string; dateTo?: string }): void {
    this.store.dispatch(PosSessionsActions.loadOverallStats({ params }));
  }

  loadSessionStats(id: string): void {
    this.store.dispatch(PosSessionsActions.loadSessionStats({ id }));
  }

  loadDetailedStats(id: string): void {
    this.store.dispatch(PosSessionsActions.loadDetailedStats({ id }));
  }

  loadHelpers(sessionId: string): void {
    this.store.dispatch(PosSessionsActions.loadHelpers({ sessionId }));
  }

  addHelper(sessionId: string, body: AddHelperDto): void {
    this.store.dispatch(PosSessionsActions.addHelper({ sessionId, body }));
  }

  removeHelper(sessionId: string, volunteerId: string): void {
    this.store.dispatch(PosSessionsActions.removeHelper({ sessionId, volunteerId }));
  }
}

